import { Caption1, Checkbox, makeStyles, tokens } from "@fluentui/react-components";
import { Add16Regular, Delete16Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { FuiButton, FuiDropdown, FuiInput, FuiTextarea } from "@/components/fui-control";
import type { GateTag, GateTagType } from "@/engine/types";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { BDD_TEMPLATE, GATE_STATUS_LABEL, GATE_TYPE_LABEL, GATE_TYPES } from "@/store/gate-ops";
import { useProjectStore } from "@/store/project-store";
import { GateTagChip } from "./GateTagChip";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM },
  gate: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS, padding: tokens.spacingVerticalS, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  gateHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacingHorizontalS },
  row: {
    display: "grid",
    gridTemplateColumns: "24px minmax(0, 1fr) auto",
    alignItems: "start",
    columnGap: "8px",
  },
  check: {
    display: "flex",
    alignItems: "center",
    height: "24px",
    minHeight: "24px",
  },
  checkIndicator: {
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
  },
  itemText: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: "24px",
    whiteSpace: "pre-wrap",
  },
  qaButtons: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    height: "24px",
  },
  actions: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: tokens.spacingHorizontalS },
  muted: { color: tokens.colorNeutralForeground3 },
  fail: { color: tokens.colorPaletteRedForeground1 },
  add: { display: "flex", gap: tokens.spacingHorizontalXS, alignItems: "center" },
  typePick: { width: "148px", flex: "0 0 148px" },
  grow: { flex: 1, minWidth: 0 },
});

/** 게이트 하나의 체크리스트·검수 조작. 드로어와 미니 팝오버가 함께 쓴다. */
export function GatePanel({ taskId, gate, readOnly, compact }: { taskId: string; gate: GateTag; readOnly?: boolean; compact?: boolean }) {
  const styles = useStyles();
  const s = useProjectStore.getState;
  const [failFor, setFailFor] = useState<string | "tag" | null>(null);
  const [reason, setReason] = useState("");
  const [draftItem, setDraftItem] = useState("");

  const submitFail = () => {
    if (!reason.trim() || !failFor) return;
    if (failFor === "tag") s().setGateVerdict(taskId, gate.id, "fail", reason);
    else s().setGateChecklistQaStatus(taskId, gate.id, failFor, "fail", reason);
    setFailFor(null);
    setReason("");
  };

  return (
    <div className={styles.root}>
      {gate.checklist.map((c) => (
        <div key={c.id} className={styles.row}>
          <Checkbox
            className={styles.check}
            indicator={{ className: styles.checkIndicator }}
            checked={c.devChecked}
            disabled={readOnly || gate.status === "passed"}
            onChange={() => s().toggleGateChecklistDev(taskId, gate.id, c.id)}
            aria-label="개발 확인"
          />
          <div>
            <div className={styles.itemText}>{c.text}</div>
            {c.qaStatus === "fail" && c.failReason && <Caption1 className={styles.fail}>반려 사유: {c.failReason}</Caption1>}
          </div>
          <div className={styles.qaButtons}>
            <FuiButton
              appearance={c.qaStatus === "pass" ? "primary" : "subtle"}
              density="dialog"
              disabled={readOnly || (gate.status !== "ready" && c.qaStatus !== "pass" && c.qaStatus !== "fail")}
              onClick={() => s().setGateChecklistQaStatus(taskId, gate.id, c.id, c.qaStatus === "pass" ? "pending" : "pass")}
            >
              통과
            </FuiButton>
            <FuiButton
              appearance={c.qaStatus === "fail" ? "primary" : "subtle"}
              density="dialog"
              disabled={readOnly || gate.status === "unverified" || gate.status === "passed"}
              onClick={() => setFailFor(c.id)}
            >
              반려
            </FuiButton>
            {!compact && !readOnly && (
              <FuiButton appearance="subtle" density="dialog" icon={<Delete16Regular />} aria-label="항목 삭제" onClick={() => s().removeGateChecklistItem(taskId, gate.id, c.id)} />
            )}
          </div>
        </div>
      ))}
      {gate.checklist.length === 0 && <Caption1 className={styles.muted}>항목이 없으면 이 검수 전체를 통과하거나 반려할 수 있습니다.</Caption1>}

      {gate.status === "failed" && gate.failReason && <Caption1 className={styles.fail}>반려 사유: {gate.failReason}</Caption1>}
      {gate.verifiedBy && gate.status !== "unverified" && (
        <Caption1 className={styles.muted}>
          {GATE_STATUS_LABEL[gate.status]} · {gate.verifiedBy}
        </Caption1>
      )}

      {failFor && (
        <div className={styles.add}>
          <FuiTextarea className={styles.grow} value={reason} onChange={(_, d) => setReason(d.value)} placeholder="반려 사유" rows={2} />
          <FuiButton appearance="primary" density="dialog" disabled={!reason.trim()} onClick={submitFail}>
            반려
          </FuiButton>
          <FuiButton appearance="subtle" density="dialog" onClick={() => setFailFor(null)}>
            취소
          </FuiButton>
        </div>
      )}

      {!readOnly && (
        <div className={styles.actions}>
          {(gate.status === "unverified" || gate.status === "failed") && (
            <FuiButton appearance="primary" density="dialog" onClick={() => s().requestGateReview(taskId, gate.id)}>
              {gate.status === "failed" ? "수정 후 재검수 요청" : "검수 요청"}
            </FuiButton>
          )}
          {gate.status === "ready" && (
            <>
              <FuiButton appearance="primary" density="dialog" onClick={() => s().setGateVerdict(taskId, gate.id, "pass")}>
                통과
              </FuiButton>
              <FuiButton appearance="outline" density="dialog" onClick={() => setFailFor("tag")}>
                반려
              </FuiButton>
            </>
          )}
        </div>
      )}

      {!compact && !readOnly && gate.status !== "passed" && (
        <div className={styles.add}>
          <FuiInput
            className={styles.grow}
            density="dialog"
            value={draftItem}
            onChange={(_, d) => setDraftItem(d.value)}
            placeholder="검수 기준 추가"
            onKeyDown={(e) => {
              if (e.key === "Enter" && draftItem.trim()) {
                s().addGateChecklistItem(taskId, gate.id, draftItem);
                setDraftItem("");
              }
            }}
          />
          <FuiButton
            appearance="subtle"
            density="dialog"
            onClick={() => {
              s().addGateChecklistItem(taskId, gate.id, draftItem.trim() || BDD_TEMPLATE);
              setDraftItem("");
            }}
          >
            {draftItem.trim() ? "추가" : "예시 문장"}
          </FuiButton>
        </div>
      )}
    </div>
  );
}

/** 드로어 안의 게이트 상세 패널. */
export function GateChecklistSection({ taskId, gates }: { taskId: string; gates: GateTag[] }) {
  const styles = useStyles();
  const surface = usePlanSurface();
  const readOnly = !surface.editTask;
  const [type, setType] = useState<GateTagType>("qa");

  return (
    <div className={styles.root}>
      {gates.map((g) => (
        <div key={g.id} className={styles.gate}>
          <div className={styles.gateHead}>
            <GateTagChip gate={g} />
            {!readOnly && (
              <FuiButton appearance="subtle" density="dialog" icon={<Delete16Regular />} aria-label="검수 삭제" onClick={() => useProjectStore.getState().removeGateTag(taskId, g.id)} />
            )}
          </div>
          <GatePanel taskId={taskId} gate={g} readOnly={readOnly} />
        </div>
      ))}
      {gates.length === 0 && <Caption1 className={styles.muted}>검수가 없으면 바로 완료할 수 있습니다.</Caption1>}
      {!readOnly && (
        <div className={styles.add}>
          <div className={styles.typePick}>
            <FuiDropdown
              density="dialog"
              value={type}
              aria-label="검수 종류"
              options={GATE_TYPES.map((t) => ({ value: t, label: GATE_TYPE_LABEL[t] }))}
              onValueChange={(v) => setType(v as GateTagType)}
            />
          </div>
          <FuiButton appearance="subtle" density="dialog" icon={<Add16Regular />} onClick={() => useProjectStore.getState().addGateTag(taskId, type)}>
            검수 추가
          </FuiButton>
        </div>
      )}
    </div>
  );
}
